import _ from 'lodash';
import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
const log = require('debug')('@mycure/vue-plugins/' + nms);

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').MedicalPatient} MedicalPatient
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} GetPatientsActionOpts
 * @property {string} organization - Organization#id
 * @property {string} searchString - text for searching patients
 * @property {string} configInsurer - active Organization#configInsurer#insurer
 * @property {boolean} includeOrganizationChildren - flag for searching patients from org children
 * @property {boolean|string[]} populateAccount - if array of string, selected fields
 * @property {boolean|string[]} populatePersonalDetails - if array of string, selected fields
 * @property {boolean|string[]} populateMedicalNote - if array of string, selected fields
 * @property {boolean|string[]} populateFacility - if array of string, selected fields
 * @property {number} [limit=50] - for pagination
 * @property {number} skip - for pagination
 *
 * @callback GetPatientsAction
 * @param {ActionContext} ctx
 * @param {GetPatientsActionOpts} opts
 * @returns {Promise<Account>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetPatientsAction}
 */
export const getPatients = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  if (opts.searchString) opts.searchString = opts.searchString.trim();
  if (!opts.organization) throw new Error(`${nms}: Organization filter is required`);

  log('#getPatients: opts', opts);

  // build projection
  const requiredSelects = [ 'id', 'facility' ];
  if (!Array.isArray(opts.populatePersonalDetails)) {
    opts.populatePersonalDetails = [ 'picURL', 'name', 'sex', 'dateOfBirth', 'mobileNo' ];
  }
  opts.populatePersonalDetails = _.uniq(requiredSelects.concat(opts.populatePersonalDetails).filter(Boolean));

  // fetch
  const query = {
    type: 'medical-patients',
    facility: opts.organization,
    $sort: { id: 1 }, // for consistent pagination
    $select: opts.populatePersonalDetails,
    $populate: {
      facility: {
        service: 'organizations',
        localKey: 'facility',
        foreignKey: 'id'
      },
      patient: {
        service: 'medical-patients',
        localKey: 'id',
        foreignKey: 'id'
      }
    },
    $comment: 'vue-plugins/patients/getPatients'
  };

  // pagination
  if (typeof opts.limit === 'number') query.$limit = opts.limit;
  if (typeof opts.skip === 'number') query.$skip = opts.skip;

  // searchstring
  const text = opts.searchString;
  const configInsurer = opts.configInsurer;
  if (typeof text === 'string' && text) {
    query.$search = _.pickBy({
      text,
      insurer: configInsurer
    });
  }

  // includeOrganizationChildren
  const includeOrganizationChildren = opts.includeOrganizationChildren;
  if (typeof includeOrganizationChildren === 'boolean') {
    if (!query.$search) query.$search = {};
    // apply filtering of facility in $search for accommodating patients accross organization
    query.$search.organization = opts.organization;
    query.$search.includeOrganizationChildren = includeOrganizationChildren;

    // remove filtering of facility in top level query
    delete query.facility;
  }

  // fetch and map
  const patients = await sdk.personalDetails().find(query).then(({ items }) => items.map(item => {
    const { $populated: { patient, ...restPopulated }, ...personalDetails } = item;
    return { ...patient, ...restPopulated, personalDetails };
  }));
  return patients;
};
